import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { goalsApi } from '@/lib/api/client';
import toast from 'react-hot-toast';

export const goalKeys = {
  all: ['goals'] as const,
  credit: (months: number) => [...goalKeys.all, 'credit', months] as const,
  monthLines: (month: string) => [...goalKeys.all, 'lines', month] as const,
};

/**
 * The credit roll-up for the last `months` months.
 *
 * `staleTime` is a minute: the figures move only when a trade does, and the dashboard tile and the
 * analytics table both mount this — a shorter window would re-run the month aggregation on every
 * page switch for numbers that cannot have changed.
 */
export function useCreditGoal(months: number) {
  return useQuery({
    queryKey: goalKeys.credit(months),
    queryFn: () => goalsApi.getCredit(months),
    staleTime: 60_000,
  });
}

/** What one month's figures are made of. Idle until a month is actually open. */
export function useCreditMonthLines(month: string | null) {
  return useQuery({
    queryKey: goalKeys.monthLines(month ?? ''),
    queryFn: () => goalsApi.getMonthLines(month ?? ''),
    enabled: month !== null,
    staleTime: 60_000,
  });
}

/**
 * Both mutations invalidate `goalKeys.all`, not just the window they were fired from: the standing
 * goal and an override change the `goal`, `remaining`, `hit` and pace of months in EVERY window,
 * so invalidating one `months` key would leave the dashboard tile showing the old target.
 */
export function useSetCreditGoal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (goal: number | null) => goalsApi.setGoal(goal),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: goalKeys.all });
      toast.success(result.goal === null ? 'Monthly credit goal cleared' : 'Monthly credit goal saved');
    },
    onError: (error: Error) => toast.error(error.message || 'Could not save the goal'),
  });
}

export function useSetCreditGoalOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ month, target }: { month: string; target: number | null }) =>
      goalsApi.setOverride(month, target),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: goalKeys.all });
      toast.success(
        result.target === null
          ? `${result.month} back on the standing goal`
          : `${result.month} goal set`,
      );
    },
    onError: (error: Error) => toast.error(error.message || 'Could not save the month goal'),
  });
}
